import ThemeSettingsModal from "components/themeSettingsModal/ThemeSettingsModal";
import {
  cssVars,
  CssVars,
  defaultDarkVars,
  ThemeSettingsValue,
  Theme,
  validateVars,
} from "components/themeSettingsModal/themeSettingsUtils";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const ThemeContext = createContext<null | {
  theme: Theme;
  customTheme: CssVars;
  openThemeModal: () => void;
  setThemeOverride: (value: ThemeSettingsValue | null) => void;
  clearThemeOverride: () => void;
}>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const storedTheme = localStorage.getItem("theme");
      if (
        storedTheme === "light" ||
        storedTheme === "dark" ||
        storedTheme === "custom"
      ) {
        return storedTheme;
      }
    } catch (e) {
      //
    }

    return "dark";
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [themeOverride, setThemeOverride] = useState<ThemeSettingsValue | null>(
    null,
  );

  const [customTheme, setCustomTheme] = useState<CssVars>(() => {
    try {
      const storedCustomThemeString = localStorage.getItem("customTheme");
      if (storedCustomThemeString) {
        const storedCustomThemeObject = JSON.parse(storedCustomThemeString);
        if (!validateVars(storedCustomThemeObject)) {
          return storedCustomThemeObject;
        }
      }
    } catch (e) {
      // console.error("Failed to read custom theme", e);
    }
    return defaultDarkVars;
  });

  const openThemeModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeThemeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const clearThemeOverride = useCallback(() => {
    setThemeOverride(null);
  }, []);

  const onThemeSettingsChange = useCallback(
    ({
      theme: nextTheme,
      customTheme: nextCustomTheme,
    }: {
      theme: Theme;
      customTheme: CssVars;
    }) => {
      setTheme(nextTheme);
      setCustomTheme(nextCustomTheme);
    },
    [],
  );

  const showDisplayThemeOverrideMessage = React.useMemo(() => {
    if (!themeOverride || typeof window === "undefined") {
      return false;
    }

    return (
      window.location.pathname.includes("/display") ||
      window.location.pathname.includes("/remote/")
    );
  }, [themeOverride]);

  useEffect(() => {
    try {
      const activeTheme = themeOverride?.theme ?? theme;
      const activeCustomTheme = themeOverride?.customTheme ?? customTheme;

      if (
        activeTheme === "light" ||
        activeTheme === "dark" ||
        activeTheme === "custom"
      ) {
        document.documentElement.setAttribute("data-theme", activeTheme);
      } else {
        document.documentElement.removeAttribute("data-theme");
      }

      if (activeTheme === "custom" && !validateVars(activeCustomTheme)) {
        const style = cssVars
          .map((cssVar) =>
            cssVar ? `--${cssVar}:${activeCustomTheme[cssVar]};` : "",
          )
          .join("");

        document.documentElement.setAttribute("style", style);
      } else {
        document.documentElement.removeAttribute("style");
      }
    } catch (e) {
      console.error("Failed to update custom theme style attribute", e);
    }
  }, [customTheme, theme, themeOverride]);

  useEffect(() => {
    try {
      const storedTheme = localStorage.getItem("theme");
      if (theme && storedTheme !== theme) {
        localStorage.setItem("theme", theme);
      }
    } catch (e) {
      console.log("Failed to set theme provider in localStorage", e);
    }
  }, [theme]);

  useEffect(() => {
    try {
      if (theme === "custom" && !validateVars(customTheme)) {
        localStorage.setItem("customTheme", JSON.stringify(customTheme));
      }
    } catch (e) {
      console.error("Failed to store custom theme", e);
    }
  }, [customTheme, theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        customTheme,
        openThemeModal,
        setThemeOverride,
        clearThemeOverride,
      }}
    >
      <ThemeSettingsModal
        isOpen={modalOpen}
        onRequestClose={closeThemeModal}
        value={{ theme, customTheme }}
        onChange={onThemeSettingsChange}
        infoMessage={
          showDisplayThemeOverrideMessage
            ? "This page is currently using a display theme override. Changes here update your system-wide theme, but the display page configuration will continue to override what you see."
            : undefined
        }
      />
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
